/* ========================= */
/* PRODUCT CONFIGURATION UI  */
/* SAME STYLE AS PRODUCT MASTER */
/* ========================= */

import React, { useEffect, useState } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API } from "../../apiConfig";
import { useParams, useNavigate, useLocation } from "react-router-dom";

const API_BASE = `${MASTER_API}/product-config`;

// const classTypeOptions = [
//   { value: "Sales Class", label: "Sales Class" },
//   { value: "Datasheet Class", label: "Datasheet Class" },
//   { value: "MRP Class", label: "MRP Class" }
// ];

const calculationTypeOptions = [
  { value: "Quantity Based Calculation", label: "Quantity Based Calculation" },
  { value: "Match", label: "Match" },
  { value: "Over Tolerance", label: "Over Tolerance" },
  { value: "Under Tolerance", label: "Under Tolerance" },
  { value: "Tolerance Checkbox", label: "Tolerance Checkbox" },
  { value: "Text", label: "Text" },
  { value: "Numeric", label: "Numeric" }
];

/* ================================================================= */
/* INPUT TYPE ENFORCEMENT                                             */
/* A characteristic tagged "Text" or "Numeric" in Product              */
/* Configuration should restrict what can be typed into its Fast      */
/* Entry field wherever that characteristic is used (Enquiry, Sales   */
/* Order, RFQ, etc). Defined once here — the master that owns these   */
/* characteristics — and imported by every screen that renders a      */
/* characteristic input, same pattern as calculateToleranceRange.     */
/* ================================================================= */

/**
 * Returns the input restriction implied by a characteristic's
 * calculation_type string (comma-separated, e.g. "Numeric" or
 * "Text,Tolerance Checkbox"). Characteristics with neither tag (e.g.
 * "Quantity Based Calculation") are left unrestricted so existing
 * behaviour doesn't change.
 * @param {string} calculationType
 * @returns {"text"|"numeric"|null}
 */
export function getCharacteristicInputType(calculationType) {
  const types = (calculationType || "")
    .split(",")
    .map((t) => t.trim().toLowerCase());
  if (types.includes("numeric")) return "numeric";
  if (types.includes("text")) return "text";
  return null;
}

/**
 * Validates a single typed value against a characteristic's declared
 * input type. Empty string is always allowed (so the user can clear
 * the field while typing); "numeric" accepts digits with at most one
 * decimal point and an optional leading minus sign; "text" rejects
 * digits entirely. Use this in an input's onChange to reject/ignore
 * keystrokes that don't match, e.g.:
 *
 *   const inputType = getCharacteristicInputType(char.calculation_type);
 *   onChange={(e) => {
 *     if (isValidCharacteristicInput(inputType, e.target.value)) {
 *       updateCharacteristic(idx, "value", e.target.value);
 *     }
 *   }}
 *
 * @param {"text"|"numeric"|null} inputType
 * @param {string} value
 * @returns {boolean}
 */
export function isValidCharacteristicInput(inputType, value) {
  if (value === "") return true;
  if (inputType === "numeric") return /^-?\d*\.?\d*$/.test(value);
  if (inputType === "text") return !/\d/.test(value);
  return true;
}

/* ================================================================= */
/* TOLERANCE RANGE CALCULATOR                                        */
/* Shared logic for the "Over Tolerances" / "Under Tolerances"       */
/* characteristics used in Fast Entry (Enquiry, and eventually       */
/* Sales Order / RFQ). Defined here (Product Configuration) since    */
/* this is the master that owns these characteristics, and imported  */
/* wherever it needs to be used.                                     */
/*                                                                     */
/* Checkbox CHECKED (tolerance_applied = true):                       */
/*   base = Length of the Drum/Coil                                   */
/*   Range = base - (length * underTolerance%)  to  base + (length * overTolerance%) */
/*   (percentage applied against Length itself, not qty)             */
/*                                                                     */
/* Checkbox UNCHECKED:                                                 */
/*   base = No. of Drum/Coil * Length of the Drum/Coil                */
/*   Range = base - (qty * underTolerance%)  to  base + (qty * overTolerance%) */
/* ================================================================= */
/**
 * @param {Object} params
 * @param {boolean} params.toleranceApplied - state of the linked Over/Under Tolerances checkbox
 * @param {number} params.length            - "Length of the Drum/Coil" characteristic value
 * @param {number} params.noOfDrum          - "No. of Drum/Coil" characteristic value
 * @param {number} params.qty               - quantity the tolerance % is applied against (unchecked case only)
 * @param {number} params.overPercent       - "Over Tolerances" value (e.g. 2 for 2%)
 * @param {number} params.underPercent      - "Under Tolerances" value (e.g. 2 for 2%)
 * @returns {{ rangeMin: number|null, rangeMax: number|null, rangeText: string }}
 */
export function calculateToleranceRange({
  toleranceApplied,
  length,
  noOfDrum,
  qty,
  overPercent,
  underPercent
}) {
  const lengthVal = Number(length) || 0;
  const noOfDrumVal = Number(noOfDrum) || 0;
  const qtyVal = Number(qty) || 0;
  const overPct = Number(overPercent) || 0;
  const underPct = Number(underPercent) || 0;
  const canCalculate = toleranceApplied
    ? lengthVal > 0
    : noOfDrumVal > 0 && lengthVal > 0;
  if (!canCalculate) {
    return { rangeMin: null, rangeMax: null, rangeText: "" };
  }
  const base = toleranceApplied ? lengthVal : noOfDrumVal * lengthVal;
  // Checked: tolerance % is applied against Length itself.
  // Unchecked: tolerance % is applied against qty (unchanged).
  const multiplier = toleranceApplied ? lengthVal : qtyVal;
  const rangeMin = base - (multiplier * underPct) / 100;
  const rangeMax = base + (multiplier * overPct) / 100;
  return {
    rangeMin,
    rangeMax,
    rangeText: `${rangeMin} - ${rangeMax}`
  };
}

/**
 * Convenience wrapper that pulls the four inputs straight out of a
 * characteristics array (the same shape used in Enquiry / Sales Order /
 * RFQ Fast Entry), so callers don't need to re-find each characteristic
 * by name themselves.
 *
 * @param {Array} characteristics - array of { characteristic, value, tolerance_applied }
 * @param {number} qty - resolved quantity for this line item
 */
export function calculateToleranceRangeFromCharacteristics(characteristics, qty) {
  const find = (test) =>
    (characteristics || []).find((c) => test((c.characteristic || "").trim().toLowerCase()));
  const overChar = find((n) => n === "over tolerances");
  const underChar = find((n) => n === "under tolerances");
  const lengthChar = find((n) => n.includes("length") && (n.includes("drum") || n.includes("coil")));
  const noOfDrumChar = find((n) => /no\.?\s*of\s*(drum|coil)/.test(n));
  const toleranceApplied = !!(overChar?.tolerance_applied || underChar?.tolerance_applied);
  return calculateToleranceRange({
    toleranceApplied,
    length: lengthChar?.value,
    noOfDrum: noOfDrumChar?.value,
    qty,
    overPercent: overChar?.value,
    underPercent: underChar?.value
  });
}

function ProductConfiguration() {

  const [classType, setClassType] = useState(null);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState(null);

  // ——— MAIN SCREEN FILTERS ———
  const [filterClassType, setFilterClassType] = useState(null);
  const [filterProductType, setFilterProductType] = useState(null);

  const [classCode, setClassCode] = useState("");
  const [description, setDescription] = useState("");
  const [characteristics, setCharacteristics] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("characteristics");
  const [changeLogs, setChangeLogs] = useState([]);

  const { classCodeParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* ========================= */
  /* INIT                      */
  /* ========================= */

  useEffect(() => {
    fetchConfigs();
    fetchProductTypes();
    fetchClassTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (classCodeParam && configs.length > 0) {
      const selected = configs.find((c) => c.class_code === classCodeParam);
      if (selected) openView(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classCodeParam, configs]);

  /* ========================= */
  /* FETCH                     */
  /* ========================= */

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API_BASE}/getProductConfigs`);
      setConfigs(res.data || []);
    } catch {
      toast.error("Failed to fetch configurations");
    }
  };

  const fetchProductTypes = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/product-types`);
      setProductTypes(
        (res.data || []).map((p) => ({ value: p.name, label: p.name }))
      );
    } catch {
      toast.error("Failed to fetch Product Types");
    }
  };

  const fetchClassTypes = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/dynamic/class-type`);
      setClassTypeOptions(
        (res.data || []).map((c) => ({ value: c.name, label: c.name }))
      );
    } catch {
      toast.error("Failed to fetch Class Types");
    }
  };

  const fetchChangeLogs = async (code) => {
    try {
      const res = await axios.get(`${API_BASE}/change-logs/${code}`);
      setChangeLogs(res.data || []);
    } catch {
      setChangeLogs([]);
    }
  };

  /* ========================= */
  /* MODAL HELPERS             */
  /* ========================= */

  const resetForm = () => {
    setSelectedId(null);
    setClassType(null);
    setSelectedProductType(null);
    setClassCode("");
    setDescription("");
    setCharacteristics([]);
    setChangeLogs([]);
    setActiveTab("characteristics");
  };

  const openCreate = () => {
    resetForm();
    setModalMode("create");
    setIsModalOpen(true);
  };

  const openView = (row) => {
    setSelectedId(row.id);
    setClassType({ value: row.class_type, label: row.class_type });
    setSelectedProductType(
      row.product_type
        ? { value: row.product_type, label: row.product_type }
        : null
    );
    setClassCode(row.class_code);
    setDescription(row.description);
    setCharacteristics(row.characteristics || []);
    setActiveTab("characteristics");
    fetchChangeLogs(row.class_code);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);

    // If we were opened via deep-link from Product Master (or elsewhere),
    // navigate back to that screen instead of staying on this page.
    const queryParams = new URLSearchParams(location.search);
    const returnTo = queryParams.get("returnTo");

    if (returnTo) {
      // Came here from Product Master (or elsewhere) — keep using router
      // navigation so we actually land on that screen. DO NOT change this.
      navigate(decodeURIComponent(returnTo));
    } else if (classCodeParam) {
      // Opened directly via /product-configuration/:classCodeParam with no
      // returnTo — just clean the URL back to the base list page.
      // NOTE: We intentionally do NOT use navigate()/router routing here.
      // Doing a full router navigation to the bare list path was tripping
      // the route guard and logging the user out. Since we're already on
      // this page and just want the URL bar to drop the trailing id,
      // silently rewrite the URL without triggering a route change.
      window.history.replaceState(
        null,
        "",
        "/cable/product/product-configuration"
      );
    }
  };

  /* ========================= */
  /* CHARACTERISTICS           */
  /* ========================= */

  const addCharacteristic = () => {
    setCharacteristics((prev) => [
      ...prev,
      { characteristic: "", calculation_type: "", tolerance_checkbox: false }
    ]);
  };

  const removeCharacteristic = (idx) => {
    setCharacteristics((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCharacteristic = (idx, field, value) => {
    setCharacteristics((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item))
    );
  };

  /* ========================= */
  /* SAVE / UPDATE             */
  /* ========================= */

  const handleSave = async () => {
    try {

      const payload = {
        id: selectedId,
        class_type: classType?.value || "",
        product_type: selectedProductType?.value || "",
        class_code: classCode,
        description,
        characteristics,
        createdBy: "Admin",
        updatedBy: "Admin"
      };

      if (modalMode === "create") {
        await axios.post(`${API_BASE}/saveProductConfig`, payload);
        toast.success("Saved Successfully");
      } else {
        await axios.put(`${API_BASE}/updateProductConfig`, payload);
        toast.success("Updated Successfully");
      }

      closeModal();
      fetchConfigs();

    } catch {
      toast.error("Failed to save");
    }
  };

  /* ========================= */
  /* DELETE                    */
  /* ========================= */

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete class "${row.class_code}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/deleteProductConfig/${row.id}`);
      toast.success("Deleted Successfully");
      fetchConfigs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  /* ========================= */
  /* STYLES                    */
  /* ========================= */

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 32, // Changed from fixed height to minHeight
      fontSize: "13px",
      borderColor: "#dee2e6",
      boxShadow: "none"
    }),
    valueContainer: (base) => ({ ...base, minHeight: "32px", padding: "0 8px" }), // Changed to minHeight
    indicatorsContainer: (base) => ({ ...base, minHeight: "32px" }), // Changed to minHeight
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
  };

  // Same as customSelectStyles but with a FIXED height (like Product Master's
  // filter row) instead of minHeight, so the filter selects sit at a fixed
  // compact height rather than growing slightly.
  const filterSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 32,
      height: 32,
      fontSize: "13px",
      borderColor: "#dee2e6",
      boxShadow: "none"
    }),
    valueContainer: (base) => ({
      ...base,
      height: "32px",
      padding: "0 8px",
      display: "flex",
      alignItems: "center"
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
  };

/* ========================= */
  /* FILTERED LIST              */
  /* ========================= */

  const filteredConfigs = configs.filter((row) => {
    const matchesClassType =
      !filterClassType || row.class_type === filterClassType.value;
    const matchesProductType =
      !filterProductType || row.product_type === filterProductType.value;
    return matchesClassType && matchesProductType;
  });

  /* ========================= */
  /* RENDER                    */
  /* ========================= */

  return (

    <Layout>

      {/* ——— PAGE HEADER ——— */}

      <div style={styles.pageTitleBar}>

        <div>
          <h2 style={styles.pageTitle}>Product Configuration</h2>
          <p style={styles.pageSubtitle}>Manage Product Classes</p>
        </div>

        <button
          className="btn btn-dark"
          style={{ fontSize: 13 }}
          onClick={openCreate}
        >
          + Create Class
        </button>

      </div>

      {/* ——— FILTERS ——— */}

      <div style={{ ...styles.tableContainer, marginBottom: 16 }}>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>

          <div style={{ width: 180, flex: "0 0 auto" }}>
            <label style={styles.compactLabel}>Class Type</label>
            <Select
              styles={filterSelectStyles}
              options={classTypeOptions}
              value={filterClassType}
              onChange={setFilterClassType}
              placeholder="All"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div style={{ width: 180, flex: "0 0 auto" }}>
            <label style={styles.compactLabel}>Product Type</label>
            <Select
              styles={filterSelectStyles}
              options={productTypes}
              value={filterProductType}
              onChange={setFilterProductType}
              placeholder="All"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

        </div>

      </div>

      {/* ——— TABLE ——— */}

      <div style={styles.tableContainer}>

        <table className="table" style={styles.erpTable}>

          <thead>
            <tr>
              <th style={styles.erpTh}>Class Type</th>
              <th style={styles.erpTh}>Product Type</th>
              <th style={styles.erpTh}>Class Code</th>
              <th style={styles.erpTh}>Description</th>
              <th style={styles.erpTh}></th>
            </tr>
          </thead>

          <tbody>
            {filteredConfigs.length > 0 ? (
              filteredConfigs.map((row) => (
                <tr key={row.id}>
                  <td style={styles.erpTd}>{row.class_type}</td>
                  <td style={styles.erpTd}>{row.product_type || "-"}</td>
                  <td style={styles.erpTd}>{row.class_code}</td>
                  <td style={styles.erpTd}>{row.description}</td>
                  <td style={styles.erpTd}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                        onClick={() => openView(row)}
                      >
                        View
                      </button>
                      <button
                        title="Delete"
                        style={{
                          background: "#fff",
                          border: "1px solid #f5c2c7",
                          borderRadius: "4px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px 8px",
                          cursor: "pointer"
                        }}
                        onClick={() => handleDelete(row)}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                          <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted p-3">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>

        </table>

      </div>

      {/* ——— MODAL ——— */}

      {isModalOpen && (

        <div style={styles.modalBackdrop}>

          <div style={{ ...styles.modal, width: "95%" }}>

            {/* MODAL HEADER */}

            <div style={styles.modalHeader}>

              <h3 style={{ margin: 0, color: "#1e293b", fontSize: "18px", fontWeight: 600 }}>
                {modalMode === "create"
                  ? "Create Product Class"
                  : modalMode === "edit"
                    ? "Edit Product Class"
                    : "View Product Class"}
              </h3>

              <button onClick={closeModal} style={styles.iconBtn}>✖</button>

            </div>

            {/* MODAL BODY */}

            <div style={styles.modalBody}>

              {/* ——— TOP CARD (always visible) ——— */}

              <div className="card p-3 mb-3 bg-light border-0">

                {/* ROW 1 */}
                <div className="row g-3 mb-2">

                  <div className="col-md-4">
                    <label style={styles.compactLabel}>Class Type</label>
                    <Select
                      styles={customSelectStyles}
                      options={classTypeOptions}
                      value={classType}
                      onChange={setClassType}
                      isDisabled={modalMode === "view"}
                      placeholder="Select"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="col-md-4">
                    <label style={styles.compactLabel}>Product Type</label>
                    <Select
                      styles={customSelectStyles}
                      options={productTypes}
                      value={selectedProductType}
                      onChange={setSelectedProductType}
                      isDisabled={modalMode === "view"}
                      placeholder="Select"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                </div>

                {/* ROW 2 */}
                <div className="row g-3">

                  <div className="col-md-4">
                    <label style={styles.compactLabel}>Class Code</label>
                    <input
                      type="text"
                      className="form-control"
                      style={styles.compactInput}
                      value={classCode}
                      onChange={(e) => setClassCode(e.target.value)}
                      disabled={modalMode === "view"}
                    />
                  </div>

                  <div className="col-md-8">
                    <label style={styles.compactLabel}>Description</label>
                    <input
                      type="text"
                      className="form-control"
                      style={styles.compactInput}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={modalMode === "view"}
                    />
                  </div>

                </div>

              </div>

              {/* ——— TABS ——— */}

              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>

                <button
                  onClick={() => setActiveTab("characteristics")}
                  style={{
                    padding: "5px 16px",
                    fontSize: 13,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 500,
                    border: activeTab === "characteristics" ? "1px solid #1e293b" : "1px solid #cbd5e1",
                    background: activeTab === "characteristics" ? "#1e293b" : "#f8fafc",
                    color: activeTab === "characteristics" ? "#fff" : "#475569"
                  }}
                >
                  Characteristics
                </button>

                {/* Only show Change Log tab in view/edit mode, not on create */}
                {modalMode !== "create" && (
                  <button
                    onClick={() => setActiveTab("changelog")}
                    style={{
                      padding: "5px 16px",
                      fontSize: 13,
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 500,
                      border: activeTab === "changelog" ? "1px solid #1e293b" : "1px solid #cbd5e1",
                      background: activeTab === "changelog" ? "#1e293b" : "#f8fafc",
                      color: activeTab === "changelog" ? "#fff" : "#475569"
                    }}
                  >
                    Change Log
                  </button>
                )}

              </div>

              {/* ——— CHARACTERISTICS TAB ——— */}

              {activeTab === "characteristics" && (

                <div style={styles.tableContainer}>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      marginBottom: 12
                    }}
                  >

                    {modalMode !== "view" && (
                      <button
                        className="btn btn-dark btn-sm"
                        onClick={addCharacteristic}
                      >
                        + Add
                      </button>
                    )}

                  </div>

                  <div style={{ overflowX: "auto" }}>

                    <table className="table" style={{ ...styles.erpTable, minWidth: "900px" }}>

                      <thead>
                        <tr>
                          <th style={styles.erpTh}>Characteristic</th>
                          <th style={styles.erpTh}>Calculation Type</th>
                          <th style={styles.erpTh}></th>
                        </tr>
                      </thead>

                      <tbody>
                        {characteristics.length > 0 ? (
                          characteristics.map((char, idx) => {
                            return (
                            <tr key={idx}>

                              <td style={styles.erpTd}>
                                <input
                                  type="text"
                                  className="form-control"
                                  style={styles.compactInput}
                                  value={char.characteristic || ""}
                                  onChange={(e) =>
                                    updateCharacteristic(idx, "characteristic", e.target.value)
                                  }
                                  disabled={modalMode === "view"}
                                />
                              </td>

                              <td style={styles.erpTd}>
                                <Select
                                  isMulti
                                  styles={customSelectStyles}
                                  options={calculationTypeOptions}
                                  value={
                                    char.calculation_type
                                      ? char.calculation_type.split(',').map(val =>
                                        calculationTypeOptions.find((o) => o.value === val.trim())
                                      ).filter(Boolean)
                                      : []
                                  }
                                  onChange={(selectedOptions) => {
                                    const val = selectedOptions
                                      ? selectedOptions.map(opt => opt.value).join(',')
                                      : "";
                                    updateCharacteristic(idx, "calculation_type", val);
                                    // "Tolerance Checkbox" being selected here (alongside
                                    // Over/Under Tolerance) is what tells Enquiry to show
                                    // the checkbox for this characteristic. Derived straight
                                    // from the same dropdown — no separate control needed.
                                    const selectedValues = val.split(",").map((v) => v.trim());
                                    const toleranceChecked = selectedValues.includes("Tolerance Checkbox");
                                    updateCharacteristic(idx, "tolerance_checkbox", toleranceChecked);
                                  }}
                                  isDisabled={modalMode === "view"}
                                  placeholder="Select"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                />
                              </td>

                              <td style={styles.erpTd}>
                                {modalMode !== "view" && (
                                  <button
                                    title="Remove"
                                    style={{
                                      background: "#fff",
                                      border: "1px solid #f5c2c7",
                                      borderRadius: "4px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "4px 8px",
                                      cursor: "pointer"
                                    }}
                                    onClick={() => removeCharacteristic(idx)}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                    </svg>
                                  </button>
                                )}
                              </td>

                            </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-muted p-3">
                              No Characteristics Added
                            </td>
                          </tr>
                        )}
                      </tbody>

                    </table>

                  </div>

                </div>

              )}

              {/* ——— CHANGE LOG TAB ——— */}

              {activeTab === "changelog" && (

                <div style={styles.tableContainer}>

                  <table className="table" style={styles.erpTable}>

                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Date & Time</th>
                        <th style={styles.erpTh}>User</th>
                        <th style={styles.erpTh}></th>
                        <th style={styles.erpTh}>Field</th>
                        <th style={styles.erpTh}>Previous Value</th>
                        <th style={styles.erpTh}>Modified Value</th>
                        <th style={styles.erpTh}>Comment</th>
                      </tr>
                    </thead>

                    <tbody>
                      {changeLogs.length > 0 ? (
                        changeLogs.map((log, idx) => (
                          <tr key={idx}>
                            <td style={styles.erpTd}>
                              {new Date(log.created_at).toLocaleString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit", hour12: true
                              })}
                            </td>
                            <td style={styles.erpTd}>{log.created_by || "-"}</td>
                            <td style={styles.erpTd}>{log.action_type || "-"}</td>
                            <td style={styles.erpTd}>{log.field_name || "-"}</td>
                            <td style={styles.erpTd}>{log.old_value || "-"}</td>
                            <td style={styles.erpTd}>{log.new_value || "-"}</td>
                            <td style={styles.erpTd}>{log.comment || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center text-muted p-3">
                            No Change Logs Found
                          </td>
                        </tr>
                      )}
                    </tbody>

                  </table>

                </div>

              )}

            </div>

            {/* MODAL FOOTER */}

            <div style={styles.modalFooter}>

              {modalMode === "view" ? (
                <button
                  className="btn btn-dark"
                  onClick={() => setModalMode("edit")}
                >
                  Edit
                </button>
              ) : (
                <button
                  className="btn btn-dark"
                  onClick={handleSave}
                >
                  Save
                </button>
              )}

            </div>

          </div>

        </div>

      )}

      <ToastContainer />

    </Layout>

  );
}

const styles = {

  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "2px",
    color: "#334155"
  },

  compactInput: {
    fontSize: "13px",
    height: "32px",
    padding: "4px 8px"
  },

  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
  },

  erpTable: {
    width: "100%",
    borderCollapse: "collapse"
  },

  erpTh: {
    background: "#f8fafc",
    fontSize: 12,
    fontWeight: 600,
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b"
  },

  erpTd: {
    fontSize: 13,
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155"
  },

  pageTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16
  },

  pageTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#1e293b"
  },

  pageSubtitle: {
    fontSize: 12,
    color: "#64748b"
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050
  },

  modal: {
    background: "#fff",
    borderRadius: "8px",
    maxWidth: "1200px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column"
  },

  modalHeader: {
    padding: "12px 20px",
    borderBottom: "1px solid #ddd",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },

  iconBtn: {
    background: "transparent",
    border: "none",
    fontSize: "16px",
    cursor: "pointer"
  },

  modalBody: {
    padding: "20px",
    overflowY: "auto"
  },

  modalFooter: {
    padding: "12px 20px",
    borderTop: "1px solid #ddd",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px"
  }

};

export default ProductConfiguration;